import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  MotifButton,
  MotifProgressLoader,
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifIcon,
  MotifErrorMessage,
  MotifTabNavigation,
  MotifTabControl,
} from "@ey-xd/motif-react";
import {
  IconoirDownload,
  IconoirUpload,
  IconoirTimer,
  IconoirArrowLeft,
} from "@ey-xd/motif-icon";
import noOrderAccessImg from "../../assets/images/noOrderAccess.svg";
import checkoutEmptyImg from "../../assets/images/checkout-empty-state.svg";
import {
  fetchConsumptionListData,
  callProcessTransaction,
  fetchConsumptionExportData,
} from "../../store/slices/ConsumptionSlice";
import SearchInput from "../../components/SearchInput/SearchInput";
import "./Consumption.scss";
import NoSearchResult from "../../components/NoSearchResult/NoSearchResult";
import ConsumptionSummary from "./ConsumptionSummary/ConsumptionSummary";
import ConsumptionTable from "./ConsumptionTable/ConsumptionTable";
import { Toast } from "../../components/Toast/Toast";
import ConsumptionImport from "./ConsumptionImport/ConsumptionImport";
import { containsEmoji } from "../../utils/common";
import {
  EMOJI_ERROR_MESSAGE,
  NO_ACCESS_CONSUMPTION,
  NO_ACCESS_CONSUMPTION_SUGGESTION,
  NO_ACCESS_CONSUMPTION_SUGGESTION2,
} from "../../Constant";
import Breadcrumb from "../../components/Breadcrumb";
import { ApiStatus } from "../../utils/apiStatus.enum";
import { useNavigate } from "react-router";
import Button from "../../components/Button/Button";

const Consumption = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [showTransactionPopup, setShowTransactionPopup] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [transactionError, setTransactionError] = useState("");
  const [consumptionListData, setConsumptionListData] = useState<any>([]);
  const [transactionSummaryData, setTransactionSummaryData] = useState<any>([]);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [exportErrorMessage, setExportErrorMessage] = useState("");
  const [exportSuccessMessage, setExportSuccessMessage] = useState("");
  const [isCSVLoading, setIsCSVLoading] = useState(false);
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [noAccess, setNoAccess] = useState<boolean>(false);
  const isLoading = useSelector(
    (state: RootState) => state.ConsumptionSlice.isLoading,
  );

  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );

  const userPermissionsStatus: ApiStatus = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissionsStatus,
  );

  const data = useSelector((state: RootState) => state.ConsumptionSlice.data);
  const error = useSelector((state: RootState) => state.ConsumptionSlice.error);
  const navigate = useNavigate();
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    const payload = { search: debouncedSearchValue, tab: activeTab };
    dispatch(fetchConsumptionListData(payload));
  }, [dispatch, debouncedSearchValue]);

  useEffect(() => {
    dispatch(fetchConsumptionListData({ search: "", tab: activeTab }));
  }, [activeTab]);

  const handleDisplayConsumption = useMemo(() => {
    if (userPermissionsStatus !== ApiStatus.SUCCESS) return false;

    if (!userPermissionsData?.length) return false;

    const skuPermissions = [
      "product_commownerproxy",
      "product_commowner",
      "system_bmo",
    ];
    return userPermissionsData.some((permission) =>
      skuPermissions.includes(permission),
    );
  }, [userPermissionsData, userPermissionsStatus]);

  useEffect(() => {
    if (
      !handleDisplayConsumption &&
      !userPermissionsData?.includes("sku_consumptionmgr")
    ) {
      setNoAccess(true);
    }
  }, [handleDisplayConsumption, userPermissionsData]);

  useEffect(() => {
    if (data?.length > 0) {
      const newConsumptionListData = data.map((product: any) => ({
        ...product,
        isExpanded: false,
        SkuDetails: product.SkuDetails.map((sku: any) => ({
          ...sku,
          isExpanded: false,
          Transactions: sku.Transactions.map((txn: any) => ({
            ...txn,
            Quantity: activeTab === 1 ? "" + txn.Quantity : "", // Clear Quantity for pending tab
          })),
        })),
      }));
      setConsumptionListData(newConsumptionListData);
    }
  }, [data, setConsumptionListData]);

  useEffect(() => {
    if (exportErrorMessage || exportSuccessMessage) {
      const timer = setTimeout(() => {
        if (exportErrorMessage) setExportErrorMessage("");
        if (exportSuccessMessage) setExportSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [exportErrorMessage, exportSuccessMessage]);

  const handleSearch = (searchTerm: string) => {
    setSearchValue(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    dispatch(fetchConsumptionListData({ search: "", tab: activeTab }));
  };

  const isAnyUnitEntered = consumptionListData.some((product: any) =>
    product.SkuDetails.some((sku: any) =>
      sku.Transactions.some(
        (txn: any) => txn.Quantity && txn.Quantity.trim() !== "",
      ),
    ),
  );

  const isAnyUnitError = consumptionListData.some((product: any) =>
    product.SkuDetails.some((sku: any) =>
      sku.Transactions.some((txn: any) => txn.Quantity.endsWith(".")),
    ),
  );

  const handleRefresh = () => {
    const payload = { search: searchValue, tab: activeTab };
    dispatch(fetchConsumptionListData(payload));
  };
  const handleProcessTransaction = async () => {
    try {
      setTransactionError("");
      setExportErrorMessage("");
      setExportSuccessMessage("");
      const requestPayload: { TransactionId: number; Quantity: number }[] = [];
      consumptionListData.forEach((product: any) => {
        product.SkuDetails.forEach((sku: any) => {
          sku.Transactions.forEach((txn: any) => {
            if (txn.Quantity && txn.Quantity.trim() !== "") {
              requestPayload.push({
                TransactionId: txn.TransactionId,
                Quantity: Number(txn.Quantity),
              });
            }
          });
        });
      });
      setIsTransactionLoading(true);
      const response = await dispatch(callProcessTransaction(requestPayload));
      if (response.payload.Status === "success") {
        setIsTransactionLoading(false);
        closeTransactionPopup();
        setIsProcessed(true);
        setTransactionSummaryData(response.payload.Data);
      } else {
        closeTransactionPopup();
        setIsTransactionLoading(false);
        setTransactionError(
          response.payload.message ||
            "An error occurred while performing the operation. Please try again. If the problem persists, please contact the support desk.",
        );
      }
    } catch (error) {
      closeTransactionPopup();
      setIsTransactionLoading(false);
      console.error("Failed to place order:", error);
      setTransactionError(
        "An error occurred while performing the operation. Please try again. If the problem persists, please contact the support desk.",
      );
    }
  };

  const closeTransactionPopup = () => {
    setShowTransactionPopup(false);
    document.body.style.overflow = "";
  };

  const downloadConsumptionFile = async () => {
    setExportSuccessMessage("");
    setTransactionError("");
    setExportErrorMessage("");
    setIsCSVLoading(true);
    try {
      const payload = { Action: "Export", Search: searchValue };
      const response = await dispatch(fetchConsumptionExportData(payload));
      if (response.payload?.message) {
        setIsCSVLoading(false);
        setExportSuccessMessage("");
        setExportErrorMessage("Something went wrong. Please try again.");
      } else {
        setExportErrorMessage("");
        const blob = new Blob([response.payload], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const today = new Date();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const yyyy = today.getFullYear();
        const fileName = `EY CH Consumption Data Extract ${mm}${dd}${yyyy}.csv`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsCSVLoading(false);
        setExportSuccessMessage("The CSV File has been downloaded succesfully");
      }
    } catch (error) {
      setIsCSVLoading(false);
      console.error("Failed to place order:", error);
      setExportSuccessMessage("");
      setExportErrorMessage("Something went wrong. Please try again.");
    }
  };

  if (isProcessed) {
    return <ConsumptionSummary transactionListData={transactionSummaryData} />;
  }

  if (noAccess) {
    return (
      <div className="consumption-no-access-state">
        <img src={noOrderAccessImg} alt="Empty Checkout Page" />
        <div>
          <p>{NO_ACCESS_CONSUMPTION}</p>
          <p className="suggestion-text">{NO_ACCESS_CONSUMPTION_SUGGESTION}</p>
          <p className="suggestion-text">{NO_ACCESS_CONSUMPTION_SUGGESTION2}</p>
        </div>
        <Button
          variant="primary"
          className="consumption-no-access-state-btn"
          onClickHandler={() => navigate("/")}
          label="Go to home"
        />
      </div>
    );
  }

  const breadcrumbItems = [
    {
      label: "External Submission",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "External Submission",
      value: "Checkout",
      elementKey: "submission",
      text: "SKU consumption",
      url: `/consumption`,
      disabled: true,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  const onTabChange = (key: number) => {
    setSearchValue("");
    setActiveTab(key);
  };

  return (
    <>
      <div className="consumption-detail-wrapper">
        <section className="consumption-content-header commercial-hub-header-banner-lg">
          <div className="consumption-breadcrumb">
            <Breadcrumb
              items={breadcrumbItems}
              ariaLabel="Breadcrumb navigation"
            />
          </div>
          <h1 className="motif-h1-default-regular consumption-title">
            SKU consumption
          </h1>
          <div className="motif-body2-default-light consumption-description">
            Add the quantity used for each transaction and submit. Only records
            with entered data will be processed.
          </div>
        </section>
        <section className="consumption-content-body">
          <div className="consumption-search-section">
            <SearchInput
              id={"consumption-search-details"}
              aria-label={"Search"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const isContainEmoji = containsEmoji(e.target.value);
                if (isContainEmoji) {
                  setSearchValidationError(EMOJI_ERROR_MESSAGE);
                } else {
                  setSearchValidationError("");
                }
                handleSearch(e.target.value);
              }}
              clearSearch={handleClearSearch}
              onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const isContainEmoji = containsEmoji(e.currentTarget.value);
                if (!isContainEmoji) {
                  handleSearch(e.currentTarget.value);
                }
              }}
              placeholder={"Search by order, product, SKU, engagement"}
              value={searchValue}
              className="consumption-search-input"
            />
            <div>
              {searchValidationError && (
                <MotifErrorMessage className="search-helper-text">
                  {searchValidationError}
                </MotifErrorMessage>
              )}
            </div>
          </div>
          <MotifTabNavigation
            defaultActiveKey={0}
            orientation="horizontal"
            size="sm"
            variant="default"
            className="consumption-tab-navigation"
          >
            <MotifTabControl onClick={() => onTabChange(0)}>
              Pending Consumption
            </MotifTabControl>
            <MotifTabControl onClick={() => onTabChange(1)}>
              Submitted Consumption
            </MotifTabControl>
          </MotifTabNavigation>
          {isLoading && (
            <MotifProgressLoader
              aria-busy="true"
              aria-label="This page is loading"
              aria-live="polite"
              arialabel="This page is loading"
              show
              variant="default"
              className="consumption-progress"
            />
          )}
          {!isLoading && (
            <>
              {activeTab === 0 && (
                <div className="consumption-upload-section">
                  <div className="upload-section-description">
                    Enter the data manually below, or download the ‘Pending’
                    .CSV template to complete it in Excel and upload it back.
                    Note: The filters above will apply to the downloaded file.
                  </div>
                  <div className="csv-upload-section">
                    <MotifButton
                      className="csv-button"
                      onClick={downloadConsumptionFile}
                      variant="secondary"
                      disabled={isCSVLoading}
                    >
                      {isCSVLoading ? "Downloading CSV" : "Download CSV"}
                      <MotifIcon
                        iconFunction={
                          isCSVLoading ? IconoirTimer : IconoirDownload
                        }
                        size="24"
                        title="Download CSV"
                        fill="#5F5F72"
                      />
                    </MotifButton>
                    <MotifButton
                      className="upload-button"
                      onClick={() => {
                        setShowUploadModal(true);
                      }}
                      variant="secondary"
                    >
                      Upload
                      <MotifIcon
                        iconFunction={IconoirUpload}
                        size="24"
                        title="Upload"
                        fill="#5F5F72"
                      />
                    </MotifButton>
                  </div>
                </div>
              )}

              {(exportErrorMessage || exportSuccessMessage) && (
                <Toast
                  variantType={exportErrorMessage ? "error" : "success"}
                  message={exportErrorMessage || exportSuccessMessage}
                  timer={3000}
                />
              )}
              {error && (
                <div className="empty-consumption-section">
                  <div>
                    <img src={checkoutEmptyImg} alt="error consumption" />
                  </div>
                  <div className="motif-body1-default-regular error-consumption-header">
                    An error has occurred while attempting to retrieve the data.
                    Please choose the option to refresh the page.
                  </div>
                  <div>
                    <MotifButton
                      className="refresh-button"
                      onClick={handleRefresh}
                    >
                      Refresh
                    </MotifButton>
                  </div>
                </div>
              )}
              {!error && !searchValue && data?.length === 0 && (
                <div className="empty-consumption-section">
                  <div>
                    <img src={checkoutEmptyImg} alt="Empty consumption List" />
                  </div>
                  <div className="motif-body1-default-regular empty-consumption-header">
                    There is no consumption transactions to review at this time.
                  </div>
                </div>
              )}
              {!error && searchValue && (!data || data.length === 0) && (
                <NoSearchResult searchValue={searchValue} />
              )}
              {data && data?.length > 0 && (
                <>
                  <ConsumptionTable
                    consumptionListData={consumptionListData}
                    setConsumptionListData={setConsumptionListData}
                    isSummary={false}
                    activeTab={activeTab}
                  />
                  {transactionError && (
                    <Toast
                      variantType="error"
                      message={transactionError}
                      timer={3000}
                    />
                  )}
                  <div
                    className={`process-transaction-btn-wrapper ${activeTab === 1 ? "hide" : ""}`}
                  >
                    <MotifButton
                      type="button"
                      disabled={!isAnyUnitEntered || isAnyUnitError}
                      onClick={() => setShowTransactionPopup(true)}
                    >
                      Submit Transaction
                    </MotifButton>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
      <MotifModal
        show={showTransactionPopup}
        onClose={closeTransactionPopup}
        className="consumption-process-modal"
        size="md"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader>Process transaction</MotifModalHeader>
        <MotifModalBody>
          <div className="process-modal-content">
            <p>
              By processing this data, the transaction(s) will be sent for
              billing.
            </p>
            <p>Do you wish to continue?</p>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton onClick={closeTransactionPopup} variant="secondary">
            Cancel
          </MotifButton>
          <MotifButton
            loading={isTransactionLoading}
            disabled={isTransactionLoading}
            onClick={handleProcessTransaction}
          >
            Continue
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
      {showUploadModal && (
        <ConsumptionImport
          showUploadModal={showUploadModal}
          setShowUploadModal={setShowUploadModal}
          setIsProcessed={setIsProcessed}
          setTransactionSummaryData={setTransactionSummaryData}
        />
      )}
    </>
  );
};
export default Consumption;
